import { useEffect, useMemo } from "react"
import { useQuery, useZero as useZeroReact } from "@rocicorp/zero/react"
import { flush as flushSentry } from "@sentry/react"
import { useNavigate } from "@tanstack/react-router"
import { create } from "zustand" // move to lib/states
import { persist } from "zustand/middleware"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import type { Schema } from "@productlane/zero"
import { decodeZeroJwt } from "@productlane/lib/src/browser"
import { schema } from "@productlane/zero"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ZeroReportGate } from "./zero-connection-report"
import type { Zero } from "@rocicorp/zero"
import type { UseNavigateResult } from "@tanstack/react-router"
import { env, isDevEnvironment } from "@/env"
import { changelogPagePreloadQueries } from "@/screens/app/changelog/screen"
import { customersPagePreloadQueries } from "@/screens/app/customers/screen"
import { helpCenterPagePreloadQueries } from "@/screens/app/helpCenter/screen"
import { roadmapPagePreloadQueries } from "@/screens/app/roadmap/screen"
import { tagsPagePreloadQueries } from "@/screens/app/tags/screen"
import { api } from "./api"
import { captureError } from "./capture-error"
import { useSession } from "./session"
import { useSetupWorkspace } from "./setup-workspace"
import { withSingleSettings } from "./single-settings"
import { useSortState } from "./states/global-state"
import {
	buildZeroFailureReport,
	isBrowserStorageFailure,
	isStaleSchemaReload,
	readZeroConnectionSnapshot,
	shouldReportZeroFailure,
	zeroFailureKey,
} from "./zero-connection-report"
import { createZeroLogSink } from "./zero-log-sink"
import {
	claimPokeReload,
	isPokeProcessingFailure,
	isStorageLostFailure,
} from "./zero-poke-recovery"
import { probeZeroStorage, resolveWithin } from "./zero-storage-probe"
import { canReuseZeroToken } from "./zero-token-reuse"

export {
	mutateConfirmed,
	MutationUnconfirmedError,
	mutateOk,
	reportServerRejection,
} from "./zero-mutate"

const { sink: zeroLogSink, buffer: zeroLogBuffer } = createZeroLogSink({
	consoleLevel: isDevEnvironment ? "debug" : "warn",
})

const zeroReportGate: ZeroReportGate = { key: null, at: 0 }

interface AuthState {
	token: string | null
	decodedToken: ZeroJwtPayload | null
	setToken: (token: string | null) => void
	setDecodedToken: (token: ZeroJwtPayload | null) => void
	clear: () => void
}

const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			token: null,
			decodedToken: null,
			setToken: (token) => set({ token }),
			setDecodedToken: (decodedToken) => set({ decodedToken }),
			clear: () => set({ token: null, decodedToken: null }),
		}),
		{
			name: "zero-store",
		},
	),
)

export const useAuthData = () => {
	const authStore = useAuthStore()
	if (!authStore.decodedToken) {
		throw new Error("decodedToken is required")
	}
	return authStore.decodedToken
}

export function useZeroConfig({
	setIsInitialized,
}: {
	setIsInitialized: (isInitialized: boolean) => void
}) {
	const navigate = useNavigate()
	const authStore = useAuthStore()
	const { session } = useSession()
	const { startSetup, setStep, step, isSettingUpWorkspace } =
		useSetupWorkspace()
	const token = authStore.token

	const userWorkspacesQuery = api.user.listWorkspaces.useQuery(
		{ role: undefined },
		{
			enabled: false,
			retry: false,
		},
	)

	const changeWorkspaceMutation = api.auth.changeWorkspace.useMutation({
		onSuccess: () => {
			// After changing workspace, retry getting token
			tokenMutation.mutate()
		},
	})

	const tokenMutation = api.auth.getZeroToken.useMutation({
		onSuccess: (data) => {
			if (data?.token) {
				authStore.setToken(data.token)
			}
		},
		onError: (error) => {
			if (error.data?.code === "UNAUTHORIZED") {
				void navigate({ to: "/logout" })
			}
		},
	})

	// Fetch token when session changes
	useEffect(() => {
		if (!session) return

		// Session exists but has no workspaceId, so the user has no workspace yet.
		// Skip the token flow entirely and go straight to setup.
		if (!session.workspaceId) {
			// User just created a workspace mid-wizard. The session hasn't picked up
			// the new workspaceId yet and a lagging listWorkspaces read can come back
			// empty, so never snap back to step 0 here while setup is in flight.
			const isMidSetup = isSettingUpWorkspace || step > 0
			userWorkspacesQuery
				.refetch()
				.then((result) => {
					if (result.data && result.data.length > 0) {
						// Session is stale: the user has a workspace, so switch to it
						changeWorkspaceMutation.mutate({
							workspaceId: result.data[0]!.id,
						})
						return
					}
					if (isMidSetup) return
					startSetup()
					setStep(0)
					void navigate({ to: "/setup-workspace", replace: true })
				})
				.catch(() => {
					if (isMidSetup) return
					startSetup()
					setStep(0)
					void navigate({ to: "/setup-workspace", replace: true })
				})
			return
		}

		if (token) {
			const decodedToken = decodeZeroJwt(token)
			if (decodedToken.success) {
				if (canReuseZeroToken(decodedToken.data, session)) {
					authStore.setDecodedToken(decodedToken.data)
					return
				}
			}
		}
		// Token missing, invalid, or minted for another user, workspace or write mode
		tokenMutation.mutate()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session])

	// Update shouldRender when token changes
	useEffect(() => {
		if (token) {
			const decodedToken = decodeZeroJwt(token)
			if (decodedToken.success) {
				authStore.setDecodedToken(decodedToken.data)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token])

	const decodedToken = authStore.decodedToken

	// Memoize the entire config object - only recreate when the userID or token actually changes
	const config = useMemo(() => {
		if (!decodedToken) {
			return null
		}

		// console.log(
		// 	"📦 useZeroConfig returning config for userID:",
		// 	decodedToken.sub,
		// )

		return {
			userID: decodedToken.sub,
			mutators,
			context: decodedToken,
			mutateURL: env.VITE_ZERO_MUTATE_URL,
			queryURL: env.VITE_ZERO_GET_QUERIES_URL,
			logLevel: isDevEnvironment ? ("debug" as const) : ("info" as const),
			logSink: zeroLogSink,
			auth: token ?? "",
			cacheURL: env.VITE_ZERO_URL,
			schema,
			init: (zero: Zero<Schema>) =>
				initializeZero(zero, {
					startSetup,
					setStep,
					navigate,
					setIsInitialized,
					refreshToken: async () => {
						return new Promise<string>((resolve, reject) => {
							tokenMutation.mutate(undefined, {
								onSuccess: (data) => {
									if (data?.token) {
										resolve(data.token)
									} else {
										reject(new Error("getZeroToken returned no token"))
									}
								},
								onError: (error) => {
									if (error.data?.code === "UNAUTHORIZED") {
										void navigate({ to: "/logout" })
									}
									reject(error)
								},
							})
						})
					},
					reportContext: {
						workspaceId: decodedToken.workspaceId,
						userId: decodedToken.userId,
					},
				}),
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [decodedToken?.sub, decodedToken?.workspaceId, token])

	return config
}

export function useZero() {
	const zero = useZeroReact<Schema>()
	return zero
}

export function useZeroWorkspace() {
	const [workspace] = useQuery(queries.currentWorkspace())
	return useMemo(
		() => (workspace ? withSingleSettings(workspace) : workspace),
		[workspace],
	)
}

/** Every connected Slack workspace, oldest first; useZeroWorkspace keeps only the newest. */
export function useSlackConnections() {
	const [workspace] = useQuery(queries.currentWorkspace())
	const rows = workspace?.slackConnectSettings
	return useMemo(
		() => [...(rows ?? [])].sort((a, b) => a.createdAt - b.createdAt),
		[rows],
	)
}

export function useZeroWorkspaceQuery() {
	const [raw, result] = useQuery(queries.currentWorkspace())
	const workspace = useMemo(() => (raw ? withSingleSettings(raw) : raw), [raw])
	return { workspace, isLoaded: result.type === "complete" }
}

const CLIENT_GROUP_ID_TIMEOUT_MS = 2000

async function reportZeroConnectionFailure(
	zero: Zero<Schema>,
	state: unknown,
	context: { workspaceId?: string | null; userId?: string | null },
) {
	const snapshot = readZeroConnectionSnapshot(state)
	const now = Date.now()
	if (!shouldReportZeroFailure(snapshot, zeroReportGate, now)) return
	if (isStaleSchemaReload(zeroLogBuffer.entries())) return
	if (isBrowserStorageFailure(zeroLogBuffer.entries())) return
	zeroReportGate.key = zeroFailureKey(snapshot)
	zeroReportGate.at = now

	// Both reads go through IndexedDB, the thing we suspect of hanging, so neither
	// may hold the report up.
	const [storage, clientGroupID] = await Promise.all([
		probeZeroStorage(),
		resolveWithin(zero.clientGroupID, CLIENT_GROUP_ID_TIMEOUT_MS),
	])

	const { error, context: errorContext } = buildZeroFailureReport({
		snapshot,
		workspaceId: context.workspaceId,
		userId: context.userId,
		clientID: zero.clientID,
		clientGroupID: typeof clientGroupID === "string" ? clientGroupID : null,
		logs: zeroLogBuffer.entries(),
		storage,
		navigatorOnline: typeof navigator === "undefined" ? null : navigator.onLine,
		visibility:
			typeof document === "undefined" ? null : document.visibilityState,
	})
	captureError(error, errorContext)
}

const POKE_RELOAD_FLUSH_MS = 2000

async function reloadAfterPokeFailure(report: Promise<void>) {
	if (!claimPokeReload(() => window.sessionStorage, Date.now())) return
	await report.catch(() => undefined)
	await flushSentry(POKE_RELOAD_FLUSH_MS)
	window.location.reload()
}

async function initializeZero(
	zero: Zero<Schema>,
	callbacks: {
		startSetup: () => void
		setStep: (step: number) => void
		navigate: UseNavigateResult<string>
		setIsInitialized: (isInitialized: boolean) => void
		refreshToken: () => Promise<string>
		reportContext: { workspaceId?: string | null; userId?: string | null }
	},
) {
	// Subscribe to connection state changes for reauthentication
	zero.connection.state.subscribe((state) => {
		const snapshot = readZeroConnectionSnapshot(state)
		if (isStorageLostFailure(snapshot, zeroLogBuffer.entries())) {
			if (claimPokeReload(() => window.sessionStorage, Date.now())) {
				window.location.reload()
			}
			return
		}
		const report = reportZeroConnectionFailure(
			zero,
			state,
			callbacks.reportContext,
		)
		if (isPokeProcessingFailure(snapshot)) void reloadAfterPokeFailure(report)
		switch (state.name) {
			case "connecting":
				console.log(`Connecting... ${state.reason}`)
				break
			case "connected":
				console.log("Connected")
				break
			case "disconnected":
				console.log(`Disconnected ${state.reason}`)
				break
			case "error":
				console.log(`Error ${state.reason}`)
				break
			case "needs-auth":
				console.log("Session expired")
				// Refresh token and reconnect when auth is needed
				void callbacks
					.refreshToken()
					.then((auth) => {
						// Retry the connection with the new token
						void zero.connection.connect({ auth })
					})
					.catch((error) => {
						console.error("Failed to refresh token:", error)
					})
				break
			default:
				break
		}
	})

	// console.log("🚀 Zero init callback started")

	// Stage 1: UI loads naturally (no preloading here)
	// console.log("📊 Stage 1: UI initial load")
	// const stage1Time = performance.now()

	// We use the current workspace as a stand-in for whtever the first UI query is.
	// They will be sent together and end together. We just need something cheap that
	// we do in fact want to preload.
	await zero.preload(queries.currentWorkspace()).complete

	// console.log(`Stage 1 complete in ${performance.now() - stage1Time}ms`)

	// console.log(`📊 Stage 2: Starting metadata preload`)
	// Stage 2 currently instant because the UI requests these same queries for
	// ThreadFilterControl. Could make startup faster by delaying those in
	// exchange for some flicker in the case where the app is loaded with filters
	// active.

	// const stage2Time = performance.now()
	// Contacts and companies stay out: a workspace can hold tens of thousands,
	// and the inbox preload below waits for this stage.
	// Issues stay out too: their descriptions are megabytes, and the inbox
	// preload below waits for this stage.
	const preloadQueries = [queries.users(), queries.projects(), queries.tags()]

	//TODO: Fix any
	const preloadPromises = preloadQueries.map(
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
		(q) => zero.preload(q as any).complete,
	)
	await Promise.all(preloadPromises)
	// console.log(`⚡️ Stage 2 complete in ${performance.now() - stage2Time}ms`)

	// Stage 3: Preload about one inbox page of threads; every row here is hydrated on each boot.
	// console.log("📊 Stage 3: Starting feedbacks + messages preload")
	// const stage3Time = performance.now()

	// Get current sort order from state
	const sortState = useSortState.getState()
	const sortColumn = sortState.sortByQueryColumn
	const sortOrder = sortState.sortByQueryColumnOrder

	await zero.preload(
		queries.threadSearch({
			limit: 100,
			sortColumn,
			sortOrder,
		}),
	).complete
	// console.log(`⚡️ Stage 3 complete in ${performance.now() - stage3Time}ms`)

	// Stage 4: Preload sidebar navigation page data after Stage 3
	// console.log("📊 Stage 4: Starting sidebar pages preload")
	// const stage4Time = performance.now()

	// Accumulate all page preload queries
	const allPageQueries = [
		...helpCenterPagePreloadQueries(),
		...roadmapPagePreloadQueries(),
		...changelogPagePreloadQueries(),
		...tagsPagePreloadQueries(),
		...customersPagePreloadQueries(),
	]

	// Other pages warm up once the browser is idle, so they never hold up boot.
	whenIdle(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
		for (const q of allPageQueries) zero.preload(q as any)
	})
	// console.log(`⚡️ Stage 4 complete in ${performance.now() - stage4Time}ms`)
	// console.log(
	// 	"🎉 Entire preload complete in",
	// 	performance.now() - stage1Time,
	// 	"ms",
	// )

	// Check if workspaces are empty and trigger setup flow if needed
	// decodedToken.sub is in format "workspaceId:userId", so we need to extract userId
	// const userId = decodedToken.sub.split(":")[1] ?? decodedToken.userId
	const user = await zero.run(queries.currentUser())

	if (!user?.memberships?.workspaceId) {
		callbacks.startSetup()
		callbacks.setStep(0)
		void callbacks.navigate({ to: "/setup-workspace", replace: true })
	}

	callbacks.setIsInitialized(true)
	// console.log("✅ Zero initialization complete")
}

/** Runs work after the current frame settles; Safari has no requestIdleCallback. */
function whenIdle(work: () => void) {
	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(work, { timeout: 5000 })
	} else {
		setTimeout(work, 2000)
	}
}
